/**
 * Авторизация пользователей системы по данным из 1С.
 * Логин — реквизит Description справочника Catalog_Пользователи.
 * Пароль — реквизит Значение в Catalog_Пользователи_ДополнительныеРеквизиты (по свойству с заданным Свойство_Key).
 * Если у пользователя пустое Значение (пароль не задан в 1С) — доступ запрещён.
 */

const SESSION_KEY = 'app_user';
const SESSION_REF_KEY = 'app_user_refkey';
const LAST_ACTIVITY_KEY = 'app_last_activity';
// Время бездействия до повторного входа. Измените это значение, если нужен другой срок.
export const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const TOUCH_THROTTLE_MS = 30 * 1000;
let lastLocalTouch = 0;

const API_BASE = '/1c-api/odata/standard.odata/';
const USERS_CATALOG = 'Catalog_Пользователи';
const USERS_ATTR_CATALOG = 'Catalog_Пользователи_ДополнительныеРеквизиты';
const PASSWORD_PROPERTY_KEY = '981f6570-08c4-11f1-ae20-ac1f6b4129e5';

import { getFixedAuthToken, getAuthorizationHeader } from './authConfig';

function getAuthHeader(): string {
  const token = getFixedAuthToken();
  if (!token) throw new Error('Учётные данные 1С не настроены (VITE_1C_USERNAME, VITE_1C_PASSWORD в .env)');
  return getAuthorizationHeader(token);
}

interface User1C {
  Ref_Key: string;
  Description?: string;
}

interface UserAttr1C {
  Ref_Key: string;
  Значение?: string;
}

export type ValidateResult = { success: true; userRefKey: string } | { success: false; message: string };

/**
 * Загружает список пользователей из 1С (Ref_Key, Description = логин).
 */
async function fetchUsersFrom1C(): Promise<User1C[]> {
  const url = `${API_BASE}${USERS_CATALOG}?$format=json&$select=Ref_Key,Description`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: getAuthHeader(), Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Ошибка загрузки пользователей: ${res.status}`);
  const data = await res.json();
  return (data?.value ?? []) as User1C[];
}

/**
 * Загружает пароли пользователей из 1С (реквизит Значение по свойству пароля).
 * Ref_Key в ответе — ключ пользователя (владельца).
 */
async function fetchPasswordsFrom1C(): Promise<UserAttr1C[]> {
  const filter = `Свойство_Key eq guid'${PASSWORD_PROPERTY_KEY}'`;
  const url = `${API_BASE}${USERS_ATTR_CATALOG}?$format=json&$select=Ref_Key,Значение&$filter=${encodeURIComponent(filter)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: getAuthHeader(), Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Ошибка загрузки паролей: ${res.status}`);
  const data = await res.json();
  return (data?.value ?? []) as UserAttr1C[];
}

/**
 * Нормализует Ref_Key для сравнения (нижний регистр, без пробелов).
 */
function normKey(key: string): string {
  return (key || '').replace(/\s/g, '').toLowerCase();
}

/**
 * Проверяет учётные данные по данным из 1С.
 * — Находит пользователя по логину (Description).
 * — Берёт пароль из дополнительных реквизитов (Значение).
 * — Если Значение пустое — возвращает { success: false, message: 'Доступ запрещён' }.
 * — Если пароль не совпадает — возвращает { success: false, message: 'Неверный логин или пароль' }.
 */
export async function validateSystemUser(username: string, password: string): Promise<ValidateResult> {
  const login = (username || '').trim();
  const pass = password ?? '';

  if (!login) {
    return { success: false, message: 'Введите логин' };
  }

  try {
    const [users, attrs] = await Promise.all([fetchUsersFrom1C(), fetchPasswordsFrom1C()]);
    const user = users.find((u) => (u.Description || '').trim() === login);
    if (!user) {
      return { success: false, message: 'Неверный логин или пароль' };
    }

    const userKey = normKey(user.Ref_Key);
    const attrRow = attrs.find((a) => normKey(a.Ref_Key) === userKey);
    const storedPassword = (attrRow?.Значение ?? '').trim();

    if (storedPassword === '') {
      return { success: false, message: 'Доступ запрещён' };
    }

    if (storedPassword !== pass) {
      return { success: false, message: 'Неверный логин или пароль' };
    }

    return { success: true, userRefKey: user.Ref_Key };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Ошибка проверки доступа';
    return { success: false, message: msg };
  }
}

/**
 * Сохраняет сессию пользователя после успешного входа (логин и Ref_Key из 1С).
 */
export const setSystemSession = (username: string, userRefKey?: string): void => {
  localStorage.setItem(SESSION_KEY, username.trim());
  if (userRefKey != null && userRefKey !== '') {
    localStorage.setItem(SESSION_REF_KEY, userRefKey);
  } else {
    localStorage.removeItem(SESSION_REF_KEY);
  }
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  lastLocalTouch = Date.now();
};

/**
 * Получает имя текущего пользователя системы (или null)
 */
export const getSystemUser = (): string | null => {
  const username = localStorage.getItem(SESSION_KEY);
  const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  if (!username || !lastActivity || Date.now() - lastActivity >= SESSION_IDLE_TIMEOUT_MS) {
    // Проверка срока действия не должна удалять общую сессию: другая вкладка
    // может в этот момент завершать повторный вход и обновлять эти значения.
    return null;
  }
  return username;
};

/** Обновляет общий таймер бездействия для всех вкладок приложения. */
export const touchSystemSession = (): void => {
  if (!getSystemUser()) return;
  const now = Date.now();
  if (now - lastLocalTouch < TOUCH_THROTTLE_MS) return;
  localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  lastLocalTouch = now;
};

/**
 * Получает Ref_Key текущего пользователя в 1С (для реквизита Автор_Key и т.п.)
 */
export const getSystemUserRefKey = (): string | null => {
  return getSystemUser() ? localStorage.getItem(SESSION_REF_KEY) : null;
};

/**
 * Выход из системы
 */
export const clearSystemSession = (): void => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_REF_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_REF_KEY);
};
